import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { RUN_STAGE_LABELS, type Run, type RunStage } from '@sfpbxtestbot/contracts';
import { api } from '../lib/api.js';
import { useAsync } from '../lib/useAsync.js';
import {
  Card,
  EmptyState,
  PageHeader,
  Stat,
  StatusBadge,
  formatDuration,
  formatRelative,
} from '../components/primitives.js';

const STAGE_ROUTE: Record<RunStage, string> = {
  'api-health': '/api-health',
  'sf-setup': '/org-setup',
  'live-call': '/live-call',
  'post-call': '/post-call',
};

const STAGE_BLURB: Record<RunStage, string> = {
  'api-health': 'Is the phone platform itself healthy?',
  'sf-setup': 'Install the package and configure the org from zero.',
  'live-call': 'Place a real call and watch the components react.',
  'post-call': 'Check what the call left behind on the Task record.',
};

/**
 * Dashboard.
 *
 * One question per card: is this stage currently working? Someone should be able
 * to glance at this before a demo and know whether anything needs attention.
 */
export function DashboardPage(): ReactNode {
  const runs = useAsync(() => api.runs({ limit: 25 }), []);
  const health = useAsync(() => api.healthReports(1), []);
  const status = useAsync(() => api.status(), []);

  const latestByStage = new Map<RunStage, Run>();
  for (const run of runs.data?.runs ?? []) {
    for (const stage of run.stages) {
      if (!latestByStage.has(stage)) latestByStage.set(stage, run);
    }
  }

  const latestHealth = health.data?.reports[0];
  const active = (runs.data?.runs ?? []).find(
    (run) => run.outcome === 'running' || run.outcome === 'pending',
  );

  return (
    <div className="stack">
      <PageHeader
        title="Dashboard"
        lede="Where each part of the RocketPhone integration stands right now, and how to check it again."
      />

      {active ? (
        <Card
          title="A run is in progress"
          subtitle={`Started ${formatRelative(active.startedAt ?? active.createdAt)} on ${active.environment}.`}
          actions={
            <Link className="button button--primary button--small" to={`/runs/${active.id}`}>
              Watch it
            </Link>
          }
        >
          <div className="row" style={{ gap: 40 }}>
            <Stat value={active.totals?.passed ?? 0} label="Steps passed" tone="passed" />
            <Stat value={active.totals?.failed ?? 0} label="Steps failed" tone="failed" />
            <Stat value={active.totals?.steps ?? 0} label="Steps so far" />
          </div>
        </Card>
      ) : null}

      <div className="grid grid--2">
        {(Object.keys(STAGE_ROUTE) as RunStage[]).map((stage) => {
          const run = latestByStage.get(stage);
          const outcome =
            stage === 'api-health' && latestHealth ? latestHealth.outcome : run?.outcome;
          const when =
            stage === 'api-health' && latestHealth
              ? latestHealth.startedAt
              : (run?.startedAt ?? run?.createdAt);

          return (
            <Card
              key={stage}
              title={RUN_STAGE_LABELS[stage]}
              subtitle={STAGE_BLURB[stage]}
              actions={
                <Link className="button button--secondary button--small" to={STAGE_ROUTE[stage]}>
                  Open
                </Link>
              }
            >
              {outcome ? (
                <div className="row row--between">
                  <StatusBadge outcome={outcome} />
                  <span className="muted" style={{ fontSize: 13 }}>
                    {formatRelative(when)}
                    {run?.durationMs ? ` · took ${formatDuration(run.durationMs)}` : ''}
                  </span>
                </div>
              ) : (
                <p className="muted" style={{ fontSize: 13 }}>
                  Not run yet.
                </p>
              )}
            </Card>
          );
        })}
      </div>

      <Card
        title="Recent runs"
        subtitle="The last few end-to-end runs, newest first."
        actions={
          <Link className="button button--ghost button--small" to="/runs">
            See all
          </Link>
        }
      >
        {(runs.data?.runs.length ?? 0) === 0 ? (
          <EmptyState
            title="Nothing has run yet"
            body="Start with the PBX API health check: it needs the least setup and tells you whether the phone platform is behaving before you touch Salesforce."
            action={
              <Link className="button button--primary button--small" to="/api-health">
                Check the APIs
              </Link>
            }
          />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>When</th>
                <th>Environment</th>
                <th>Tenant</th>
                <th>Result</th>
                <th>Duration</th>
              </tr>
            </thead>
            <tbody>
              {runs.data?.runs.slice(0, 8).map((run) => (
                <tr key={run.id}>
                  <td>
                    <Link to={`/runs/${run.id}`}>{formatRelative(run.createdAt)}</Link>
                  </td>
                  <td>{run.environment}</td>
                  <td>{run.tenantCode}</td>
                  <td>
                    <StatusBadge outcome={run.outcome} />
                  </td>
                  <td>{formatDuration(run.durationMs)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {status.data ? (
        <p className="muted" style={{ fontSize: 13 }}>
          {status.data.activeRuns} of {status.data.maxConcurrentRuns} run slots in use. Runs are
          limited so that browser sessions and audio devices do not collide.
        </p>
      ) : null}
    </div>
  );
}
